import { useState } from 'react';

import { adminServices } from '../api/admin';
import ServiceBlock from '../components/ServiceBlock';
import type { ServiceArea } from '../types/content';
import { AreaField, ListField, TextField } from './fields';
import PreviewOverlay from './PreviewOverlay';
import { useCollection } from './useCollection';
import { useDirty, useUnsavedChanges } from './useUnsavedChanges';

const EMPTY: ServiceArea = {
  id: '',
  num: '',
  title: '',
  summary: '',
  problem: '',
  what: [],
  how: [],
  outcome: '',
  titleEn: '',
  summaryEn: '',
  problemEn: '',
  whatEn: [],
  howEn: [],
  outcomeEn: ''
};

export default function ServicesPanel() {
  const { items, error, reload } = useCollection(adminServices.list);
  const { confirmLeave } = useUnsavedChanges();
  const [draft, setDraft] = useState<ServiceArea | null>(null);
  const [original, setOriginal] = useState<ServiceArea | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useDirty(draft !== null && JSON.stringify(draft) !== JSON.stringify(original));

  function open(service: ServiceArea, id: string | null) {
    if (!confirmLeave()) return;
    setDraft({ ...service });
    setOriginal({ ...service });
    setEditingId(id);
    setSaveError(null);
  }

  function close() {
    setDraft(null);
    setOriginal(null);
    setEditingId(null);
  }

  function change(patch: Partial<ServiceArea>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        await adminServices.update(editingId, draft);
      } else {
        await adminServices.create(draft);
      }
      close();
      reload();
    } catch {
      setSaveError('Kunde inte spara. Kontrollera att id:t är unikt och att alla fält är ifyllda.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(service: ServiceArea) {
    if (!window.confirm(`Ta bort tjänsten "${service.title}"?`)) return;
    await adminServices.remove(service.id);
    if (editingId === service.id) {
      close();
    }
    reload();
  }

  return (
    <>
      <div className={draft ? 'admin-layout is-editing' : 'admin-layout'}>
        <div className="admin-column">
          <div className="admin-head">
            <h2>Tjänster</h2>
            <button className="button primary small" type="button" onClick={() => open(EMPTY, null)}>
              Ny tjänst
            </button>
          </div>

          {error && <div className="state is-error">{error}</div>}

          <div className="admin-list">
            {items?.map((service) => (
              <div className="admin-row" key={service.id}>
                <div>
                  <strong>
                    {service.num} {service.title}
                  </strong>
                  <div className="meta">{service.id}</div>
                </div>
                <div className="admin-actions">
                  <button
                    className="button secondary small"
                    type="button"
                    onClick={() => open(service, service.id)}
                  >
                    Redigera
                  </button>
                  <button className="button danger small" type="button" onClick={() => remove(service)}>
                    Ta bort
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {draft && (
          <div className="admin-panel">
            <div className="panel-head">
              <h3>{editingId ? `Redigerar ${editingId}` : 'Ny tjänst'}</h3>
              <button className="button secondary small" type="button" onClick={() => setPreview(true)}>
                Förhandsgranska
              </button>
            </div>
          <TextField
            label="Id"
            hint="(används i URL:en, t.ex. due-diligence)"
            value={draft.id}
            onChange={(value) => change({ id: value })}
          />
          <div className="field-row">
            <TextField label="Nummer" value={draft.num} onChange={(value) => change({ num: value })} />
            <TextField label="Titel" value={draft.title} onChange={(value) => change({ title: value })} />
          </div>
          <AreaField
            label="Kort beskrivning"
            value={draft.summary}
            onChange={(value) => change({ summary: value })}
          />
          <AreaField
            label="Kundens problem"
            value={draft.problem}
            onChange={(value) => change({ problem: value })}
          />
          <ListField label="Vad vi gör" value={draft.what} onChange={(value) => change({ what: value })} />
          <ListField label="Så arbetar vi" value={draft.how} onChange={(value) => change({ how: value })} />
          <AreaField label="Resultat" value={draft.outcome} onChange={(value) => change({ outcome: value })} />

          <div className="lang-section">
            <h4>
              Engelska <span className="hint">(valfritt – tomma fält visar svenskan)</span>
            </h4>
            <TextField
              label="Titel"
              value={draft.titleEn ?? ''}
              onChange={(value) => change({ titleEn: value })}
            />
            <AreaField
              label="Kort beskrivning"
              value={draft.summaryEn ?? ''}
              onChange={(value) => change({ summaryEn: value })}
            />
            <AreaField
              label="Kundens problem"
              value={draft.problemEn ?? ''}
              onChange={(value) => change({ problemEn: value })}
            />
            <ListField
              label="Vad vi gör"
              value={draft.whatEn ?? []}
              onChange={(value) => change({ whatEn: value })}
            />
            <ListField
              label="Så arbetar vi"
              value={draft.howEn ?? []}
              onChange={(value) => change({ howEn: value })}
            />
            <AreaField
              label="Resultat"
              value={draft.outcomeEn ?? ''}
              onChange={(value) => change({ outcomeEn: value })}
            />
          </div>

          {saveError && <div className="state is-error">{saveError}</div>}

          <div className="admin-actions">
            <button className="button primary" type="button" onClick={save} disabled={saving}>
              {saving ? 'Sparar…' : 'Spara'}
            </button>
            <button className="button secondary" type="button" onClick={() => confirmLeave() && close()}>
              Avbryt
            </button>
          </div>
        </div>
        )}
      </div>

      {draft && preview && (
        <PreviewOverlay title="Så visas tjänsten på /tjanster" onClose={() => setPreview(false)}>
          <ServiceBlock service={draft} />
        </PreviewOverlay>
      )}
    </>
  );
}
